import { LitElement, html, TemplateResult, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GridEvents } from '../../../types/common';
import { State } from '../types';
import { InlineMenuMixin } from '../../../../mixins';

export class InlineRowActionsWebComponent extends InlineMenuMixin(LitElement) {
  static get styles() {
    return styles;
  }

  @property({ type: Boolean, reflect: true })
  disabled = false;

  /**
   * When this property is set to TRUE, the edit icon will be replaced by the inlineEditControls
   */
  @property({ type: Boolean, reflect: true })
  canEdit = false;

  /**
   * When this property is set to FALSE the SAVE icon button will be disabled. This happens when the row text fields
   * are invalid
   */
  @property({ type: Boolean, reflect: true })
  canSave = true;

  @property({ type: String, reflect: true })
  state: State | null = null;

  updated(properties: PropertyValues): void {
    if (properties.has('state')) {
      this.handleStateUpdate();
    }
  }

  render() {
    return html`
      <div class="d-flex align-items-center justify-end">
        ${this.stateRenderer()} ${this.canEdit ? this.renderInlineEditControls() : this.renderInlineMenu()}
      </div>
    `;
  }

  private renderInlineEditControls() {
    return html`
      <span class="grid-edit-buttons d-flex align-items-center justify-space-between">
        <gz-icon-button
          class="grid-edit-button"
          icon="ds-icon-notification_success"
          buttonColor="success"
          buttonFill="quiet"
          fontSize="24"
          @click="${this.handleRowEditSave}"
          ?disabled="${!this.canSave}"
        ></gz-icon-button>
        <gz-icon-button
          icon="ds-icon-delete_large"
          buttonColor="secondary"
          buttonFill="quiet"
          buttonType="round"
          fontSize="24"
          class="grid-edit-button ml-1"
          @click="${this.handleRowEditCancel}"
        ></gz-icon-button>
      </span>
    `;
  }

  private handleSelectionChange(e: CustomEvent) {
    const eventName = e.detail.selected;

    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          rowData: this.itemData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private stateRenderer(): TemplateResult | null {
    switch (this.state) {
      case State.LOADING:
        return html`<gz-progress-spinner
          indeterminate
          spinnerWidth="small"
          class="spinner mr-1"
        ></gz-progress-spinner>`;

      case State.ERROR:
        return html`<gz-icon
          name="ds-icon-notification_negative_S"
          class="validation-icon mr-1"
          color="negative"
          size="24"
        ></gz-icon>`;

      case State.SUCCESS:
        return html`<gz-icon
          name="ds-icon-validation_checked"
          class="validation-icon mr-1"
          color="success"
          size="24"
        ></gz-icon>`;

      default:
        return null;
    }
  }

  public handleRowEditSave() {
    this.canEdit = false;
    this.dispatchEvent(
      new CustomEvent(GridEvents.EDIT_ROW_SAVE, {
        detail: {
          rowData: this.itemData,
        },
        bubbles: true,
      })
    );
  }

  public handleRowEditCancel() {
    this.canEdit = false;
    this.dispatchEvent(
      new CustomEvent(GridEvents.EDIT_ROW_CANCEL, {
        detail: {
          rowData: this.itemData,
        },
        bubbles: true,
      })
    );
  }

  private handleStateUpdate() {
    // The success icon is displayed for 6s
    if (this.state === State.SUCCESS) {
      setTimeout(() => {
        this.state = null;
      }, 6000);
    }
  }
}
